import React, { useContext, useEffect } from "react";
import { useTeams } from "src/scenes/teams/teams-provider";
import { Box, Checkbox, FormControlLabel, FormGroup, Stack } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import styled from "styled-components";
import Tooltip from "@mui/material/Tooltip";
import { OverviewContext } from "src/components/providers/overview-provider";
import { useSearchParams } from "react-router-dom";
import { useFeature } from "src/components/providers/feature-flags";
import { EXECUTIVE_SHOW_ALL } from "src/utils/feaure-flag-constants";
import { useOrg } from "src/components/providers/org-provider";

const StyledMenuItem = styled(MenuItem)`
    font-size: 12px;
`;

const TeamSelect = () => {
    const [searchParams] = useSearchParams();
    const teams = useTeams();
    const { org } = useOrg();

    const availableTeams =
        teams &&
        teams.status === "SUCCESS" &&
        teams.data &&
        teams.data.filter((team) => team.membersCount >= (org.minimumTeamMembers || 3));

    const nonAccessibleTeams =
        teams &&
        teams.status === "SUCCESS" &&
        teams.data &&
        teams.data.filter((team) => team.membersCount < (org.minimumTeamMembers || 3));

    const { selectedTeam, setSelectedTeam, setAggregationPeriod, devsOnly, setDevsOnly } =
        useContext(OverviewContext);

    const showFromAll = useFeature(EXECUTIVE_SHOW_ALL);

    useEffect(() => {
        if (teams.status === "SUCCESS") {
            if (!searchParams.get("selectedTeam")) {
                if (teams.data.length === 0) {
                    setSelectedTeam("all");
                    setAggregationPeriod("week");
                } else {
                    setSelectedTeam("integrated");
                }
            } else if (
                !["integrated", "all", ...teams.data.map((team) => team.id)].includes(selectedTeam)
            ) {
                setSelectedTeam("integrated");
            }
        }
    }, [teams.status]);

    const handleDevsOnlyChange = (event) => {
        setDevsOnly(event.target.checked);
    };

    const renderStyledValue = (value) => {
        if (!availableTeams) return null;
        let selected = availableTeams.find((team) => team.id === value);
        if (selected) {
            selected = { name: selected.name };
        } else if (value === "integrated") {
            selected = { name: "Company members in platform" };
        } else {
            selected = { name: "All company members" };
        }
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "13px",
                    fontWeight: "300",
                }}
            >
                {selected.name}
            </Box>
        );
    };
    const handleChange = (event) => {
        if (event.target.value === "all") setAggregationPeriod("week");
        setSelectedTeam(event.target.value);
    };
    return (
        <Box sx={{ minWidth: 350 }}>
            <Stack direction="row" spacing={2} style={{ width: "100%" }}>
                <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label" sx={{ minWidth: 2200 }} size="small">
                        Select company or team
                    </InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={selectedTeam || "integrated"}
                        label="Select company or team"
                        size="small"
                        renderValue={renderStyledValue}
                        onChange={handleChange}
                    >
                        <StyledMenuItem value="integrated">
                            Company members in platform
                        </StyledMenuItem>
                        {showFromAll && (
                            <StyledMenuItem value="all">
                                <Tooltip placement="right" title="Less data accuracy">
                                    <p style={{ margin: 0 }}>All company members</p>
                                </Tooltip>
                            </StyledMenuItem>
                        )}
                        {availableTeams &&
                            availableTeams.map((team) => {
                                return (
                                    <StyledMenuItem key={team.id} value={team.id}>
                                        {team.name}
                                    </StyledMenuItem>
                                );
                            })}
                        {nonAccessibleTeams &&
                            nonAccessibleTeams.length &&
                            nonAccessibleTeams.map((team) => {
                                return (
                                    <Tooltip
                                        placement="right"
                                        title="Less than 3 people in the team. Add more people to access"
                                    >
                                        <StyledMenuItem key={team.id} value={team.id} disabled>
                                            {team.name}
                                        </StyledMenuItem>
                                    </Tooltip>
                                );
                            })}
                    </Select>
                </FormControl>
                <FormGroup>
                    <Tooltip title="As developers are regarded the ones that did at least one commit 10% of the working days of the period (~13 days in 6 months)">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    inputProps={{ "aria-label": "Developers only" }}
                                    checked={devsOnly}
                                    onChange={handleDevsOnlyChange}
                                    size="small"
                                />
                            }
                            label={<p style={{ fontSize: "12px" }}>Active developers only</p>}
                            style={{ width: "179px" }}
                        />
                    </Tooltip>
                </FormGroup>
            </Stack>
        </Box>
    );
};

export default TeamSelect;
